import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { isPathActive } from "@/lib/routing/activePath";
import { useQueryClient } from "@tanstack/react-query";
import { EventTemplate, TemplateSectionType } from "@/types/EventTemplate";
import { updateTemplate } from "@/lib/api/handlers/EventTemplateHandling";
import { IconButton } from "@/components/Buttons/IconButton";
import { ScopedLoadingSpinner } from "@/components/LoadingSpinner/ScopedLoadingSpinner";
import {
  IconAddressBook,
  IconArrowLeft,
  IconSettings,
  IconPuzzle,
  IconUsers,
  IconShield,
  IconTable,
  IconBolt,
  IconQrcode,
  IconCalendarEvent,
  IconWorld,
  IconMail,
  IconArmchair,
  IconLayoutDashboard,
  IconCheck,
  IconMailForward,
  IconPaperclip,
  IconFileText,
  IconUserPlus,
  IconTargetArrow,
} from "@tabler/icons-react";
import { prefetchTemplateRoute } from "./routePrefetch";
import { useSaveErrorToast } from "@/components/builders/shared/useSaveErrorToast";

type TemplateSidebarProps = {
  template: EventTemplate;
  isLoading?: boolean;
};

// Stable sentinel reused by the sync effect so the dep doesn't churn on
// the `?? []` fallback path (would otherwise re-set local state every
// render and risk fighting the optimistic state in toggleSection).
const EMPTY_SECTIONS: TemplateSectionType[] = [];

const SECTION_ITEMS: {
  section: TemplateSectionType;
  icon: React.ElementType;
  url: string;
}[] = [
  { section: TemplateSectionType.GENERAL, icon: IconSettings, url: "general" },
  { section: TemplateSectionType.DASHBOARD, icon: IconLayoutDashboard, url: "dashboard" },
  { section: TemplateSectionType.GOALS, icon: IconTargetArrow, url: "goals" },
  { section: TemplateSectionType.WEBSITE, icon: IconWorld, url: "website" },
  { section: TemplateSectionType.FIELDS, icon: IconUsers, url: "fields" },
  { section: TemplateSectionType.DEVICES, icon: IconQrcode, url: "devices" },
  { section: TemplateSectionType.SLOTTING, icon: IconCalendarEvent, url: "slotting" },
  { section: TemplateSectionType.SEATING, icon: IconArmchair, url: "seating" },
  { section: TemplateSectionType.PIPES, icon: IconBolt, url: "workflows" },
  { section: TemplateSectionType.EMAIL, icon: IconMail, url: "email" },
  { section: TemplateSectionType.DOCUMENTS, icon: IconFileText, url: "documents" },
  { section: TemplateSectionType.SENDER, icon: IconMailForward, url: "sender" },
  { section: TemplateSectionType.ATTACHMENTS, icon: IconPaperclip, url: "attachments" },
  { section: TemplateSectionType.GUEST_IMPORT, icon: IconAddressBook, url: "guest-import" },
  { section: TemplateSectionType.TABLE_VIEWS, icon: IconTable, url: "table-views" },
  { section: TemplateSectionType.RULES, icon: IconShield, url: "rules" },
  { section: TemplateSectionType.COLLABORATORS, icon: IconUserPlus, url: "collaborators" },
  { section: TemplateSectionType.PLUGINS, icon: IconPuzzle, url: "plugins" },
];

type TemplateMenuRowProps = {
  item: (typeof SECTION_ITEMS)[number];
  basePath: string;
  isActive: boolean;
  isIncluded: boolean;
  isLoading: boolean;
  sectionLabel: string;
  navigateTo: (url: string) => void;
  prefetch: (url: string) => void;
  toggleSection: (section: TemplateSectionType) => void;
};

const TemplateMenuRow = memo(function TemplateMenuRow({
  item,
  basePath,
  isActive,
  isIncluded,
  isLoading,
  sectionLabel,
  navigateTo,
  prefetch,
  toggleSection,
}: TemplateMenuRowProps) {
  const fullUrl = `${basePath}/${item.url}`;
  return (
    <div
      className={`flex items-center gap-1 rounded-lg ${isActive ? "surface-secondary" : ""}`}
    >
      {item.section !== TemplateSectionType.GENERAL && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (isLoading) return;
            toggleSection(item.section);
          }}
          disabled={isLoading}
          className={`hidden md:flex w-4 h-4 rounded border items-center justify-center shrink-0 ml-2 transition-colors ${
            isIncluded
              ? "cta-primary border-transparent"
              : "outline-secondary"
          } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {isIncluded && <IconCheck size={10} className="font-light-primary" />}
        </button>
      )}
      <div
        className={`flex-1 ${
          item.section === TemplateSectionType.GENERAL ? "md:ml-7" : ""
        }`}
      >
        <IconButton
          href={fullUrl}
          onClickAction={() => navigateTo(fullUrl)}
          onHoverAction={() => prefetch(fullUrl)}
          active={false}
          disableWithoutShowing={isActive}
          color={
            isActive
              ? "font-primary"
              : isIncluded
                ? "font-secondary"
                : "font-quaternary"
          }
          fontSize="body-3-medium"
          space="space-x-0 md:space-x-1.5"
          padding="p-2.5 md:px-2 md:py-1.5"
          icon={item.icon}
          iconSize={16}
          text={sectionLabel}
          textClassName="hidden md:block"
          width="w-auto md:w-full"
        />
      </div>
    </div>
  );
});

export const TemplateSidebar: React.FC<TemplateSidebarProps> = ({
  template,
  isLoading = false,
}) => {
  const t = useTranslations("eventTemplates");
  const pathname = useLocation().pathname;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [includedSections, setIncludedSections] = useState<
    TemplateSectionType[]
  >(template.includedSections ?? EMPTY_SECTIONS);
  const [isToggling, setIsToggling] = useState(false);
  // Mirror of includedSections for toggleSection's async flow. The target set
  // must be computed OUTSIDE any setState updater: production React runs
  // updaters during render, not synchronously, so reading a variable assigned
  // inside one crashed the handler before the PUT ever fired while the
  // optimistic tick still rendered (BUGS.md C2, FIXED 2026-07-05 W2-E).
  const includedSectionsRef = useRef<TemplateSectionType[]>(
    template.includedSections ?? EMPTY_SECTIONS,
  );
  // Refcount of overlapping in-flight toggles. Boolean isToggling would
  // flip false the moment the FIRST toggle completes — letting a second
  // still-pending toggle's stale-refetch stomp the optimistic UI. Counter
  // keeps the sync-gate held until ALL pending toggles settle.
  const togglingCountRef = useRef(0);

  // Sync local state with the prop when it changes (the sidebar now
  // mounts during TemplateLayoutShell's loading window with an
  // EMPTY_TEMPLATE sentinel, so includedSections arrives later).
  // Gated on togglingCountRef so an in-flight optimistic toggle isn't
  // stomped by an invalidate-triggered refetch returning a stale set.
  useEffect(() => {
    if (togglingCountRef.current > 0) return;
    includedSectionsRef.current = template.includedSections ?? EMPTY_SECTIONS;
    setIncludedSections(template.includedSections ?? EMPTY_SECTIONS);
  }, [template.includedSections, isToggling]);

  const navigateTo = useCallback(
    (url: string) => {
      // Dispatch navigationStart so dirty-form guards (TemplateSlottingClient,
      // TemplateDevicesClient, useTemplateSection) can intercept and show the
      // leave-confirmation modal. DOM dispatchEvent is synchronous: a listener
      // dispatching navigationCancelled runs to completion before this returns,
      // so we can check the flag immediately. Mirrors useEventSidebar.
      let cancelled = false;
      const handler = () => {
        cancelled = true;
      };
      window.addEventListener("navigationCancelled", handler, { once: true });
      window.dispatchEvent(
        new CustomEvent("navigationStart", { detail: { url } }),
      );
      window.removeEventListener("navigationCancelled", handler);
      if (!cancelled) navigate(url);
    },
    [navigate],
  );

  const prefetch = useCallback((url: string) => {
    prefetchTemplateRoute(url);
  }, []);

  // Visible toast when the include-toggle PUT fails (shared H6/H17 helper).
  const failSave = useSaveErrorToast();

  const toggleSection = useCallback(
    async (section: TemplateSectionType) => {
      togglingCountRef.current += 1;
      setIsToggling(true);

      // FIXED 2026-07-05 W2-E (BUGS.md C2): the target set used to be
      // computed inside the setIncludedSections updater and read back
      // synchronously — undefined in the production build, so the handler
      // crashed before the PUT while the optimistic tick still rendered.
      // Compute from the ref, PUT first, and only reflect the toggle in the
      // UI (state + sectionToggled event) after the PUT succeeded.
      const prev = includedSectionsRef.current;
      const wasIncluded = prev.includes(section);
      const newSections = wasIncluded
        ? prev.filter((s) => s !== section)
        : [...prev, section];
      // Overlapping toggles chain off the pending target, not stale state.
      includedSectionsRef.current = newSections;

      try {
        const result = await updateTemplate(template.templateId, {
          includedSections: newSections,
        });
        if (result && "isServerError" in result) {
          // PUT failed → undo only THIS section's flip in the pending
          // target (a full reset would wipe a concurrent toggle), and say so.
          includedSectionsRef.current = wasIncluded
            ? includedSectionsRef.current.includes(section)
              ? includedSectionsRef.current
              : [...includedSectionsRef.current, section]
            : includedSectionsRef.current.filter((s) => s !== section);
          failSave(result);
        } else {
          setIncludedSections((current) =>
            wasIncluded
              ? current.filter((s) => s !== section)
              : current.includes(section)
                ? current
                : [...current, section],
          );
          window.dispatchEvent(
            new CustomEvent("sectionToggled", {
              detail: { section, included: !wasIncluded },
            }),
          );
          await queryClient.invalidateQueries({
            queryKey: ["templateLayout", template.templateId],
          });
          await queryClient.invalidateQueries({
            queryKey: ["template", template.templateId],
          });
        }
      } finally {
        togglingCountRef.current -= 1;
        if (togglingCountRef.current === 0) setIsToggling(false);
      }
    },
    [template.templateId, queryClient, failSave],
  );

  // Mobile header forwards toggle requests via this event.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.section) {
        toggleSection(detail.section as TemplateSectionType);
      }
    };
    window.addEventListener("sectionToggleRequest", handler);
    return () => window.removeEventListener("sectionToggleRequest", handler);
  }, [toggleSection]);

  const basePath = `/templates/${template.templateId}`;
  const showTemplateInfo = !!template.name;

  return (
    // min-w-0/max-w-full + overflow-x-auto on the nav: below md this renders
    // as a horizontal icon strip — enough sections exceed 375px and the
    // trailing tabs were unreachable (html clips x-overflow). Same fix as
    // UserSidebar.
    <div className="relative flex flex-row md:flex-col md:h-full max-w-full min-w-0 pr-0 pb-1 md:pb-0 md:pr-3 gap-1 shrink-0">
      <div className="hidden md:block pb-2 border-b outline-tertiary mb-1">
        <IconButton
          href="/templates"
          onClickAction={() => navigateTo("/templates")}
          onHoverAction={() => prefetch("/templates")}
          icon={IconArrowLeft}
          text={t("backToList")}
          fontSize="body-3-medium"
          padding="px-3 py-1.5"
          space="space-x-1.5"
        />
      </div>

      {showTemplateInfo && (
        <div className="hidden md:block px-1 py-2 border-b outline-tertiary mb-1">
          <p className="body-2-medium font-primary truncate">{template.name}</p>
          {template.description && (
            <p className="body-4-regular font-tertiary truncate mt-0.5">
              {template.description}
            </p>
          )}
        </div>
      )}

      <div className="flex md:hidden shrink-0 items-center">
        <IconButton
          href="/templates"
          onClickAction={() => navigateTo("/templates")}
          onHoverAction={() => prefetch("/templates")}
          icon={IconArrowLeft}
          text=""
          padding="p-2.5"
          space="space-x-0"
        />
      </div>

      <nav className="flex flex-row md:flex-col items-center md:items-stretch flex-1 min-w-0 overflow-x-auto no-scrollbar md:overflow-y-auto gap-1">
        {SECTION_ITEMS.map((item) => {
          const fullUrl = `${basePath}/${item.url}`;
          const isActive =
            isPathActive(pathname, fullUrl);
          const isIncluded = includedSections.includes(item.section);
          return (
            <TemplateMenuRow
              key={item.section}
              item={item}
              basePath={basePath}
              isActive={isActive}
              isIncluded={isIncluded}
              isLoading={isLoading}
              sectionLabel={t(`sections.${item.section}`)}
              navigateTo={navigateTo}
              prefetch={prefetch}
              toggleSection={toggleSection}
            />
          );
        })}
      </nav>

      {isToggling && <ScopedLoadingSpinner />}
    </div>
  );
};
